import type { PrV2NavigationOverlayEntrance } from '@/app/pr-v2/[owner]/[repo]/pull/[number]/components/shared/skeletons/pr-v2-navigation-loading-overlay';

/** The surface that started a client navigation to a pull request. */
export type PrOpenSource = 'inbox' | 'command_palette' | 'stack' | 'notification' | 'desktop';

export type PrNavigationStart = {
  path: string;
  /** `performance.now()` when the navigation started. */
  startedAt: number;
  source: PrOpenSource;
  /** `window-reveal` navigations start in a hidden desktop window and then show it. */
  entrance: PrV2NavigationOverlayEntrance;
};

let pendingStart: PrNavigationStart | null = null;
let hasResolvedDocumentLoadStart = false;

/**
 * Records when a surface starts a client navigation to the pull request at `href`, so the PR page
 * can measure how long it took to open. A newer navigation replaces an older one.
 */
export function recordPrNavigationStart(
  href: string,
  source: PrOpenSource,
  entrance: PrV2NavigationOverlayEntrance,
): void {
  pendingStart = {
    path: href.replace(/[?#].*$/, ''),
    startedAt: performance.now(),
    source,
    entrance,
  };
}

/**
 * Takes the recorded navigation start, returning it when it was recorded for `path`. Any PR page
 * that takes it settles it: a page for another path means that navigation was superseded.
 */
export function takePrNavigationStart(path: string): PrNavigationStart | null {
  const start = pendingStart;
  pendingStart = null;

  return start?.path === path ? start : null;
}

/**
 * Returns the document's navigation start when the first PR page of this document is the one the
 * browser loaded at `path`. Answers at most once per document, so Back or Forward to that URL later
 * is not mistaken for a page load.
 */
export function resolveDocumentLoadStart(path: string): { startedAt: number } | null {
  if (hasResolvedDocumentLoadStart) {
    return null;
  }
  hasResolvedDocumentLoadStart = true;

  const [navigationEntry] = performance.getEntriesByType('navigation');
  if (!navigationEntry || new URL(navigationEntry.name).pathname !== path) {
    return null;
  }

  // performance.now() counts from the document's navigation start.
  return { startedAt: 0 };
}
